import React from 'react'
import { FilterMatchMode } from 'primereact/api'
import {
    createCopyButtonTemplate,
    dateFormatter,
    createContactTypeBadgeTemplate,
    createCompanyTypeBadgeTemplate,
    createMembershipTypesTemplate,
    createOnHoldTemplate,
    createWorksAvatarTemplate
} from '../templates/ContactLandingColumnTemplates'

/**
 * Creates column definitions for Contact Landing grid
 * @param {object} params - Configuration parameters
 * @param {string} params.id - Component ID for element IDs
 * @param {object} params.expandedRows - Currently expanded rows
 * @param {function} params.setExpandedRows - Function to update expanded rows
 * @param {Array} params.companyTypes - Company types from assets
 * @returns {Array} Column definitions array
 */
export const createContactColumnDefinitions = ({ id, expandedRows, setExpandedRows, companyTypes = [] }) => [
    // Author Key with copy + nav button
    { 
        columnType: 'text', 
        title: 'Author Key', 
        mapsToField: 'authorKey', 
        filter: true, 
        sortable: true, 
        template: createCopyButtonTemplate(id, 'authorKey', { 
            showNavButton: true, 
            navDisabled: true 
        })
    },
    
    // Contact Name with copy + nav button
    { 
        columnType: 'text', 
        title: 'Contact Name', 
        mapsToField: 'displayName', 
        filter: true, 
        sortable: true, 
        template: createCopyButtonTemplate(id, 'displayName', { 
            showNavButton: true, 
            navDisabled: true, 
            className: 'ellipsis-text' 
        })
    },
    
    // Contact Type with badge and dropdown filter
    {
        columnType: 'contacttype',
        title: 'Contact Type',
        mapsToField: 'contactTypeName',
        filter: true,
        sortable: true,
        filterMatchMode: FilterMatchMode.EQUALS,
        template: createContactTypeBadgeTemplate()
    },
    
    // Company Type with badge and dropdown filter
    {
        columnType: 'companytype', 
        title: 'Company Type', 
        mapsToField: 'companyType', 
        filter: true, 
        sortable: true,
        filterMatchMode: FilterMatchMode.EQUALS,
        template: createCompanyTypeBadgeTemplate(companyTypes)
    },
    
    // Membership Types with badges and dropdown filter
    // Uses CONTAINS filter mode to support Match All/Match Any for multi-value fields
    {
        columnType: 'membershiptype',
        title: 'Member Type',
        mapsToField: 'memberships',
        filter: true,
        sortable: true,
        filterMatchMode: FilterMatchMode.CONTAINS,
        template: createMembershipTypesTemplate()
    },
    
    // Members Start Date (uses default DateColumn)
    { 
        columnType: 'date', 
        title: 'Members Start Date', 
        mapsToField: 'membersStartDate', 
        filterMatchMode: FilterMatchMode.DATE_IS, 
        filter: true, 
        sortable: true 
    },
    
    // Address with copy button
    {
        columnType: 'text', 
        title: 'Address', 
        mapsToField: 'displayAddress', 
        filter: true, 
        sortable: true, 
        template: createCopyButtonTemplate(id, 'displayAddress', { 
            className: 'ellipsis-text', 
            tooltip: true 
        }), 
        style: { width: '300px', maxWidth: '300px' }
    },
    
    // Postcode with copy button
    { 
        columnType: 'text', 
        title: 'Postcode', 
        mapsToField: 'postCode', 
        filter: true, 
        sortable: true, 
        template: createCopyButtonTemplate(id, 'postCode')
    },
    
    // Email with copy button
    { 
        columnType: 'text', 
        title: 'Email', 
        mapsToField: 'email', 
        filter: true, 
        sortable: true, 
        template: createCopyButtonTemplate(id, 'email', { 
            className: 'ellipsis-text' 
        })
    },
    
    // Pending Royalty (numeric)
    { 
        columnType: 'numeric', 
        title: 'Pending Royalty', 
        mapsToField: 'pendingRoyalty', 
        filter: true,
        filterMatchMode: FilterMatchMode.EQUALS,
        sortable: true 
    },
    
    // On Hold checkbox
    { 
        columnType: 'boolean', 
        title: 'On-Hold', 
        mapsToField: 'isOnHold', 
        filter: true, 
        sortable: true,
        filterMatchMode: FilterMatchMode.EQUALS,
        template: createOnHoldTemplate(id)
    },
    
    // D.O.B with date formatter + copy button
    { 
        columnType: 'date', 
        title: 'D.O.B', 
        mapsToField: 'dateOfBirth', 
        filterMatchMode: FilterMatchMode.DATE_IS, 
        filter: true, 
        sortable: true, 
        template: createCopyButtonTemplate(id, 'dateOfBirth', { 
            formatter: dateFormatter 
        })
    },
    
    // Works with avatar and expansion
    { 
        columnType: 'boolean', 
        title: 'Works', 
        mapsToField: 'hasWorks', 
        filter: true, 
        sortable: true,
        filterMatchMode: FilterMatchMode.EQUALS,
        template: createWorksAvatarTemplate(expandedRows, setExpandedRows), 
        expand: true 
    }
]
